import React, { useState } from "react";
import { 
  Mail, Phone, MapPin, Globe, ArrowRight, 
  ShieldCheck
} from "lucide-react";

const ContactUs = () => {
  const [message, setMessage] = useState("");
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    company: ""
  });

  // Handle Input Changes
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  // Function to restrict word count to 200
  const handleMessageChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const text = e.target.value;
    const words = text.trim().split(/\s+/);
    if (words.length <= 200 || text.length < message.length) {
      setMessage(text);
    }
  };

  // Function to allow only alphabets
  const allowOnlyLetters = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!/[a-zA-Z\s]/.test(e.key)) {
      e.preventDefault();
    }
  };

  // Click Handler for Submit Button
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Yahan aap apna API call ya logic likh sakte hain
    console.log("Form Submitted:", { ...formData, message });
    alert("Thank you! Your message has been sent.");
  };

  return (
    <div className="bg-white h-auto text-slate-900 font-sans selection:bg-red-500/20 overflow-x-hidden relative pt-24 md:pt-32"> 
      {/* --- REAL WORLD MAP BACKGROUND LAYER --- */}
      <div className="absolute inset-0 z-0 pointer-events-none overflow-hidden">
        <div className="absolute top-1/4 left-1/3 w-[500px] h-[500px] bg-blue-600/5 rounded-full blur-[180px]" />

        <div
          className="absolute inset-0 opacity-[0.2]"
          style={{
            backgroundImage:
              "url('https://upload.wikimedia.org/wikipedia/commons/8/80/World_map_-_low_resolution.svg')",
            backgroundRepeat: "no-repeat",
            backgroundPosition: "center",
            backgroundSize: "85%",
            filter: "invert(30%) sepia(80%) saturate(2500%) hue-rotate(190deg) brightness(80%) contrast(100%)",
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-white/10 via-white/40 to-white" />
      </div>

      <div className="relative z-10">
        <section className="relative pt-10 pb-12 px-6 text-center lg:text-left">
          <div className="max-w-7xl mx-auto">
           <h1 className="text-4xl md:text-6xl font-[1000] tracking-tighter text-slate-900 leading-[0.9]">
            Architecting the <br />
            <span className="text-red-600 italic">Global Future</span> of trade.
          </h1>
          <p className="mt-4 text-slate-500 font-medium text-sm md:text-lg max-w-lg leading-relaxed">
            Empowering businesses with seamless logistics, real-time insights, and a network that spans across borders.
          </p>
          </div>
        </section>

      <section className="pb-0 px-5 mt-2 relative z-10">
        <form onSubmit={handleSubmit} className="max-w-5xl mx-auto grid lg:grid-cols-2 gap-16 items-start">
          
          <div className="space-y-4 py-0">
            <div>
              <h3 className="text-[10px] font-black uppercase tracking-[0.3em] text-red-600 mb-8">
               Let's Scale Your Global Trade
              </h3>
              
              <div className="space-y-10">
                <div className="flex items-center gap-6 group">
                  <div className="w-12 h-12 rounded-full border border-slate-200 flex items-center justify-center text-red-600 bg-slate-50 group-hover:bg-red-600 group-hover:text-white transition-all duration-300 shadow-sm">
                    <Phone size={20} />
                  </div>
                  <p className="text-lg font-bold tracking-tight text-slate-800">+91 9650660070</p>
                </div>

                <div className="flex items-center gap-6 group">
                  <div className="w-12 h-12 rounded-full border border-slate-200 flex items-center justify-center text-red-600 bg-slate-50 group-hover:bg-red-600 group-hover:text-white transition-all duration-300 shadow-sm">
                    <Mail size={20} />
                  </div>
                  <div>
                    <p className="text-[10px] text-slate-400 font-black uppercase tracking-widest mb-1">Work Email</p>
                    <p className="text-lg font-bold tracking-tight text-slate-800">care@B2Bhaarat.com</p>
                  </div>
                </div>

                <div className="flex items-center gap-6 group">
                  <div className="w-12 h-12 rounded-full border border-slate-200 flex items-center justify-center text-red-600 bg-slate-50 group-hover:bg-red-600 group-hover:text-white transition-all duration-300 shadow-sm">
                    <MapPin size={20} />
                  </div>
                  <div>
                    <p className="text-[10px] text-slate-400 font-black uppercase tracking-widest mb-1">HQ Address</p>
                    <p className="text-lg font-bold tracking-tight text-slate-800">Sector 15, Noida, India</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="space-y-5 py-5">
            <div className="space-y-4">
              <input 
                type="text" 
                name="name"
                required
                placeholder="Your Name" 
                onKeyPress={allowOnlyLetters}
                onChange={handleInputChange}
                className="w-full bg-slate-50 border border-slate-200 rounded-xl px-5 py-4 outline-none font-bold text-slate-900 text-xs shadow-sm focus:border-red-500 transition-all placeholder:text-slate-400" 
              />
              <input 
                type="email" 
                name="email"
                required
                placeholder="Work Email" 
                onChange={handleInputChange}
                className="w-full bg-slate-50 border border-slate-200 rounded-xl px-5 py-4 outline-none font-bold text-slate-900 text-xs shadow-sm focus:border-red-500 transition-all placeholder:text-slate-400" 
              />
              <input 
                type="text" 
                name="company"
                required
                placeholder="Company" 
                onKeyPress={allowOnlyLetters}
                onChange={handleInputChange}
                className="w-full bg-slate-50 border border-slate-200 rounded-xl px-5 py-4 outline-none font-bold text-slate-900 text-xs shadow-sm focus:border-red-500 transition-all placeholder:text-slate-400" 
              />
              <div className="relative">
                <textarea 
                  rows={4} 
                  value={message}
                  required
                  onChange={handleMessageChange}
                  placeholder="Message (Max 200 words)" 
                  className="w-full bg-slate-50 border border-slate-200 rounded-xl px-5 py-4 outline-none font-bold text-slate-900 text-xs resize-none shadow-sm focus:border-red-500 transition-all placeholder:text-slate-400"
                ></textarea>
                <div className="text-[10px] text-slate-400 mt-1 text-right font-bold">
                  {message.trim() === "" ? 0 : message.trim().split(/\s+/).length} / 200 Words
                </div>
              </div>
            </div>
            
            <div className="flex justify-end pt-2">
              <button 
                type="submit"
                className="bg-red-600 text-white font-black px-10 py-4 rounded-xl flex items-center gap-3 hover:bg-red-700 transition-all duration-300 uppercase text-xs tracking-widest shadow-lg shadow-red-200 active:scale-95"
              >
                Submit Message <ArrowRight size={18} />
              </button>
            </div>
          </div>
        </form>
      </section>

      <section className="pb-10 pt-10 relative z-10">
        <div className="max-w-md mx-auto flex justify-center items-center gap-6">
          {[
            { Icon: Phone, label: "Call Us" },
            { Icon: Mail, label: "Email" },
            { Icon: Globe, label: "Website" },
            { Icon: MapPin, label: "Visit" },
            { Icon: ShieldCheck, label: "Security" }
          ].map((item, i) => (
            <div key={i} className="group flex flex-col items-center gap-2 cursor-pointer">
              <div className="w-12 h-12 rounded-full border border-red-100 bg-red-50 flex items-center justify-center text-red-600 shadow-sm group-hover:scale-110 group-hover:bg-red-600 group-hover:text-white transition-all duration-300">
                <item.Icon size={20} />
              </div>
              <span className="text-[8px] uppercase tracking-widest text-red-600/70 font-bold group-hover:text-red-600">
                {item.label}
              </span>
            </div>
          ))}
        </div>
      </section>
      </div>
    </div>
  );
};

export default ContactUs;